import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Download, FileText, ChevronDown } from "lucide-react";
import { api, formatApiError } from "../../api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Calendar } from "../ui/calendar";

const EMPTY_LINE = { description: "", quantity: 1, unit_price: 0, tva_rate: 20 };

function money(n) {
  return `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export async function downloadDevisPdf(devisId, number) {
  const res = await api.get(`/admin/devis/${devisId}/pdf`, { responseType: "blob" });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Devis-${number || devisId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export default function DevisCreateModal({ open, onOpenChange, prefill = {}, onCreated }) {
  const [client, setClient] = useState({
    client_name: prefill.client_name || "",
    client_email: prefill.client_email || "",
    client_phone: prefill.client_phone || "",
    client_address: prefill.client_address || "",
    project: prefill.project || "",
    notes: "",
  });
  const [validUntil, setValidUntil] = useState(null);
  const [openCal, setOpenCal] = useState(false);
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    let ht = 0, tva = 0;
    lines.forEach((l) => {
      const lineHt = Number(l.quantity || 0) * Number(l.unit_price || 0);
      ht += lineHt;
      tva += (lineHt * Number(l.tva_rate || 0)) / 100;
    });
    return { ht, tva, ttc: ht + tva };
  }, [lines]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, { ...EMPTY_LINE }]);
  const rmLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const submit = async (andDownload = false) => {
    if (!client.client_name) return toast.error("Nom du client requis");
    if (lines.length === 0 || lines.some((l) => !l.description)) return toast.error("Chaque ligne doit avoir une description");
    setSaving(true);
    try {
      const payload = {
        ...client,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          tva_rate: Number(l.tva_rate) || 0,
        })),
        quote_id: prefill.quote_id,
        appointment_id: prefill.appointment_id,
        valid_until: validUntil ? validUntil.toISOString().slice(0, 10) : null,
      };
      const { data } = await api.post("/admin/devis", payload);
      toast.success(`Devis ${data.number} créé`);
      if (andDownload) await downloadDevisPdf(data.id, data.number);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto border-white/10 bg-[#0d0d0d] text-white">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl uppercase tracking-tight text-white flex items-center gap-2"><FileText size={20} className="text-[#ff4d4d]" />Créer un devis</DialogTitle>
          <DialogDescription className="text-white/60">Renseignez les lignes, le PDF sera généré automatiquement.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Client *</Label>
              <Input value={client.client_name} onChange={(e) => setClient({ ...client, client_name: e.target.value })} className="border-white/10 bg-black/50" data-testid="devis-client-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Email</Label>
              <Input value={client.client_email} onChange={(e) => setClient({ ...client, client_email: e.target.value })} className="border-white/10 bg-black/50" data-testid="devis-client-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Téléphone</Label>
              <Input value={client.client_phone} onChange={(e) => setClient({ ...client, client_phone: e.target.value })} className="border-white/10 bg-black/50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Adresse</Label>
              <Input value={client.client_address} onChange={(e) => setClient({ ...client, client_address: e.target.value })} className="border-white/10 bg-black/50" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-white/70">Objet du devis</Label>
              <Input value={client.project} onChange={(e) => setClient({ ...client, project: e.target.value })} placeholder="Ex : Sonorisation soirée 6h" className="border-white/10 bg-black/50" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/70">Validité</Label>
              <Popover open={openCal} onOpenChange={setOpenCal}>
                <PopoverTrigger asChild>
                  <button type="button" className="flex w-full items-center justify-between rounded-md border border-white/10 bg-black/50 px-3 py-2 text-left text-sm text-white">
                    <span className={validUntil ? "text-white" : "text-white/40"}>{validUntil ? validUntil.toLocaleDateString("fr-FR") : "Optionnel"}</span>
                    <ChevronDown size={14} className="text-white/50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto border-white/10 bg-[#111] p-0" align="start">
                  <Calendar mode="single" selected={validUntil} onSelect={(d) => { setValidUntil(d); setOpenCal(false); }} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-white/70">Lignes du devis</Label>
              <button type="button" onClick={addLine} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white hover:bg-white/5"><Plus size={14} />Ligne</button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="grid gap-2 rounded-md border border-white/10 bg-black/40 p-3 md:grid-cols-12" data-testid={`devis-line-${i}`}>
                  <Input placeholder="Description" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className="border-white/10 bg-black/50 md:col-span-5" />
                  <Input type="number" step="0.5" placeholder="Qté" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="border-white/10 bg-black/50 md:col-span-2" />
                  <Input type="number" step="0.01" placeholder="P.U. HT" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="border-white/10 bg-black/50 md:col-span-2" />
                  <Input type="number" step="1" placeholder="TVA %" value={l.tva_rate} onChange={(e) => setLine(i, "tva_rate", e.target.value)} className="border-white/10 bg-black/50 md:col-span-2" />
                  <button type="button" onClick={() => rmLine(i)} className="rounded-md border border-red-500/30 px-2 py-1 text-red-400 hover:bg-red-500/10 md:col-span-1"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/70">Notes</Label>
            <Textarea value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} rows={2} className="border-white/10 bg-black/50" placeholder="Conditions de paiement, remarques…" />
          </div>

          <div className="rounded-md border border-white/10 bg-[#111] p-4">
            <div className="flex items-center justify-between text-sm text-white/70"><span>Sous-total HT</span><span>{money(totals.ht)}</span></div>
            <div className="mt-1 flex items-center justify-between text-sm text-white/70"><span>TVA</span><span>{money(totals.tva)}</span></div>
            <div className="mt-3 border-t border-white/10 pt-3 flex items-center justify-between"><span className="font-head text-lg uppercase text-white">Total TTC</span><span className="font-head text-2xl text-gradient font-semibold">{money(totals.ttc)}</span></div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" onClick={() => submit(false)} disabled={saving} className="rounded-full border border-white/20 px-6 py-3 text-sm text-white hover:bg-white/5 disabled:opacity-50" data-testid="devis-save-btn">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button type="button" onClick={() => submit(true)} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-6 py-3 text-sm font-semibold text-white hover:scale-[1.02] transition-transform disabled:opacity-50" data-testid="devis-save-download-btn">
              <Download size={14} />Enregistrer & Télécharger PDF
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
